// Ghana Mobile Money Integration Service

export interface MoMoProvider {
  name: string;
  code: 'MTN' | 'VODAFONE' | 'AIRTELTIGO';
  ussdCode: string;
  apiEndpoint?: string;
}

export const MOMO_PROVIDERS: MoMoProvider[] = [
  {
    name: 'MTN Mobile Money',
    code: 'MTN',
    ussdCode: '*170#',
    apiEndpoint: '/api/payments/mtn'
  },
  {
    name: 'Vodafone Cash',
    code: 'VODAFONE', 
    ussdCode: '*110#',
    apiEndpoint: '/api/payments/vodafone'
  },
  {
    name: 'AirtelTigo Money',
    code: 'AIRTELTIGO',
    ussdCode: '*185#',
    apiEndpoint: '/api/payments/airteltigo'
  }
];

export interface PaymentRequest {
  amount: number;
  phone: string;
  provider: 'MTN' | 'VODAFONE' | 'AIRTELTIGO';
  reference: string;
  description: string;
}

export interface PaymentResponse {
  success: boolean;
  transactionId?: string;
  reference?: string;
  status: 'pending' | 'completed' | 'failed';
  message: string;
  ussdPrompt?: string;
}

class MoMoService {
  private apiUrl = import.meta.env.VITE_API_URL || 'https://family-membership-system.jositett.workers.dev';

  async initiatePayment(request: PaymentRequest): Promise<PaymentResponse> {
    // Validate input parameters
    if (!request.phone || !this.validateGhanaPhone(request.phone)) {
      return {
        success: false,
        status: 'failed',
        message: 'Invalid Ghana phone number format'
      };
    }
    
    if (!request.amount || request.amount <= 0 || request.amount > 50000) {
      return {
        success: false,
        status: 'failed',
        message: 'Invalid payment amount'
      };
    }
    
    // Validate provider to prevent NoSQL injection
    const validProviders: Array<'MTN' | 'VODAFONE' | 'AIRTELTIGO'> = ['MTN', 'VODAFONE', 'AIRTELTIGO'];
    if (!validProviders.includes(request.provider)) {
      return {
        success: false,
        status: 'failed',
        message: 'Invalid payment provider'
      };
    }
    
    // Generate reference for manual transfer (no gateway integration)
    const provider = MOMO_PROVIDERS.find(p => p.code === request.provider);
    if (!provider) {
      return {
        success: false,
        status: 'failed',
        message: 'Invalid payment provider'
      };
    }

    // Return success with generated reference for manual transfer
    const generatedReference = `${request.reference}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
    
    return {
      success: true,
      status: 'pending',
      reference: generatedReference,
      transactionId: generatedReference,
      message: `Payment reference generated. Use ${provider.ussdCode} to send ${request.amount} GHS manually with reference: ${generatedReference}`,
      ussdPrompt: `Dial ${provider.ussdCode} to send money manually`
    };
  }

  async checkPaymentStatus(transactionId: string): Promise<PaymentResponse> {
    // Validate transaction ID format
    if (!transactionId || typeof transactionId !== 'string' || !/^[a-zA-Z0-9_-]+$/.test(transactionId)) {
      return {
        success: false,
        status: 'failed',
        message: 'Invalid transaction ID format'
      };
    }
    
    // Manual verification system - all payments require admin approval
    return {
      success: true,
      status: 'pending',
      message: 'Payment is pending admin verification. Manual transfers require admin approval.',
      reference: transactionId
    };
  }

  validateGhanaPhone(phone: string): boolean {
    // Ghana phone number validation
    const ghanaPhoneRegex = /^(\+233|0)(20|23|24|25|26|27|28|29|50|53|54|55|56|57|59)\d{7}$/;
    return ghanaPhoneRegex.test(phone);
  }

  formatGhanaPhone(phone: string): string {
    // Convert to international format
    if (phone.startsWith('0')) {
      return '+233' + phone.substring(1);
    }
    if (!phone.startsWith('+233')) {
      return '+233' + phone;
    }
    return phone;
  }
}

export const momoService = new MoMoService();